/**
 * Punch records — /timeclock/manage (#427 W1c, spec §5 W1c).
 *
 * Maddie's correction surface: the day's punches per staff, edit with a
 * REQUIRED reason (server writes a TimeEntryAudit row — spec D2), soft
 * void with reason, and "Add missed entry" for forgot-to-punch days.
 * Route-gated on payroll.clock:write:all (route-permission map + the
 * RouteGuard rule in route-permissions.ts).
 *
 * Design Philosophy notes:
 *  - P2: one filled-primary per view — "Add missed entry" (the page's
 *    reason to exist when something's missing); the canonical #278
 *    violation is closed by construction: staff are picked from the
 *    roster by NAME, never a raw UUID input.
 *  - P5: voided entries stay visible (struck through, with the reason)
 *    — the record is evidence, not display state; open entries wear a
 *    live "Still clocked in" badge instead of a fake out-time.
 *  - Timezone stance (api.ts wire contract): the CLIENT owns local-day
 *    math — the picker's local day is sent as explicit UTC instants.
 *  - Five states: skeleton / empty-teaching / error-recovery / default
 *    (stale n/a — every date change refetches).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ClientConfig } from '@ferro/config';
import { Modal, PageHeader } from '@ferro/ui';
import {
  adminStaffApi,
  adminTimeclockApi,
  type AdminStaffUser,
  type TimeclockBreak,
  type TimeclockEntry,
} from '../lib/api.js';
import { TimeclockAdminSection } from '../components/timeclock/TimeclockAdminSection.js';
// Sawyer 14 early-clockin-gate (E1, E3)
import {
  EarlyApprovalList,
  firstName,
} from '../components/timeclock/EarlyApprovalList.js';

/** Sawyer 14 (E3): the one sentence for a meal break outside the entry (server-identical). */
const MEAL_WINDOW_MESSAGE =
  'The meal break must fall between the clock-in and clock-out times.';

/** Sawyer 14 (E1): the early chip for an entry row, or null when not early. */
function earlyChip(entry: TimeclockEntry): { label: string; pending: boolean } | null {
  const early = entry.early;
  if (!early) return null;
  const by = early.decided_by_name ?? 'a manager';
  switch (early.status) {
    case 'pending':
      return { label: 'Early start waiting', pending: true };
    case 'approved':
      return { label: `Early start approved by ${by}`, pending: false };
    case 'trimmed':
      return {
        label: `Paid from ${fmtTime(entry.paid_from ?? early.scheduled_start_at)} (trimmed by ${by})`,
        pending: false,
      };
    default:
      return null;
  }
}

// ─── Local-day + datetime helpers ────────────────────────────────────────

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** YYYY-MM-DD of a Date in the BROWSER's timezone (the facility's day). */
function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Local-day bounds as UTC ISO instants — the client owns day math. */
function dayWindowIso(date: string): { fromIso: string; toIso: string } {
  const from = new Date(`${date}T00:00:00`);
  const to = new Date(`${date}T23:59:59.999`);
  return { fromIso: from.toISOString(), toIso: to.toISOString() };
}

/** ISO instant → value for an <input type="datetime-local"> (local tz). */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** datetime-local value → UTC ISO instant; null when empty/invalid. */
function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function fmtDayLabel(date: string): string {
  const d = new Date(`${date}T12:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00`);
  d.setDate(d.getDate() + days);
  return localDateStr(d);
}

function durationLabel(inIso: string, outIso: string): string {
  const ms = new Date(outIso).getTime() - new Date(inIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const mins = Math.round(ms / 60_000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** Human labels for punch sources — never the raw enum string. */
function sourceLabel(source: string | null): string | null {
  if (!source) return null;
  if (source === 'self') return 'Own login';
  if (source === 'kiosk') return 'Kiosk';
  if (source === 'admin') return 'Manager';
  return source;
}

function breakLine(b: TimeclockBreak): string {
  const kind = b.kind === 'meal' ? 'Meal break' : 'Rest break';
  const paid = b.paid ? '' : ' (unpaid)';
  if (!b.end_at) return `${kind}${paid} — on break now`;
  return `${kind}${paid} ${fmtTime(b.start_at)}–${fmtTime(b.end_at)}`;
}

// ─── Page ────────────────────────────────────────────────────────────────

type EditDraft = {
  entry: TimeclockEntry;
  clockIn: string; // datetime-local
  clockOut: string; // datetime-local ('' = untouched/open)
  reason: string;
};

type AddDraft = {
  staffUserId: string;
  clockIn: string;
  clockOut: string;
  reason: string;
  // Sawyer 14 (E3): optional meal break on the missed day.
  mealOn: boolean;
  mealStart: string;
  mealEnd: string;
  mealPaid: boolean;
};

export function TimeclockManage({ config }: { config: ClientConfig }): JSX.Element {
  const apiUrl = config.deployment.apiUrl;

  const [date, setDate] = useState(() => localDateStr(new Date()));
  const [entries, setEntries] = useState<TimeclockEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Audit visibility: entries corrected THIS session wear an inline
  // "Edited" chip (the server keeps the full TimeEntryAudit trail).
  const [editedIds, setEditedIds] = useState<ReadonlySet<string>>(new Set());

  // Modals own their pending/error states so the list stays interactive.
  const [editDraft, setEditDraft] = useState<EditDraft | null>(null);
  const [editPending, setEditPending] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [voidFor, setVoidFor] = useState<TimeclockEntry | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidPending, setVoidPending] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addDraft, setAddDraft] = useState<AddDraft>({
    staffUserId: '',
    clockIn: '',
    clockOut: '',
    reason: '',
    mealOn: false,
    mealStart: '',
    mealEnd: '',
    mealPaid: true,
  });
  // Sawyer 14 (E1, E3): does the server carry the early clock-in lane?
  // The settings response tells us (AC21) — an older server would drop a
  // mealBreak silently, so the meal fields and the waiting list wait for it.
  const [serverHasEarly, setServerHasEarly] = useState(false);
  const [onDutyMealPaid, setOnDutyMealPaid] = useState(true);
  const [earlyRefresh, setEarlyRefresh] = useState(0);
  const [addPending, setAddPending] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  // Roster for the add-entry staff select (#278 lesson: names, not UUIDs).
  const [staffRoster, setStaffRoster] = useState<AdminStaffUser[]>([]);
  const [rosterError, setRosterError] = useState<string | null>(null);

  const loadEntries = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    const { fromIso, toIso } = dayWindowIso(date);
    const result = await adminTimeclockApi.entries(apiUrl, fromIso, toIso);
    setIsLoading(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setEntries(result.data?.entries ?? []);
  }, [apiUrl, date]);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  // Sawyer 14 (E1, E3): one settings read for the lane flag + meal default.
  useEffect(() => {
    let cancelled = false;
    void adminTimeclockApi.getSettings(apiUrl).then((res) => {
      if (cancelled || !res.data?.settings) return;
      const settings = res.data.settings;
      setServerHasEarly('early_clock_in_allowance_minutes' in settings);
      setOnDutyMealPaid(settings.on_duty_meal_paid !== false);
    });
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  const loadRoster = useCallback(async (): Promise<void> => {
    setRosterError(null);
    const result = await adminStaffApi.getStaff(apiUrl);
    if (result.error) {
      setRosterError(result.error);
      return;
    }
    setStaffRoster((result.data?.staff ?? []).filter((s) => s.is_active));
  }, [apiUrl]);

  // Group the day's entries per staff, keeping punch order within each.
  const grouped = useMemo(() => {
    const byStaff = new Map<string, { name: string; rows: TimeclockEntry[] }>();
    for (const e of entries) {
      const g = byStaff.get(e.staff_user_id) ?? { name: e.staff_name, rows: [] };
      g.rows.push(e);
      byStaff.set(e.staff_user_id, g);
    }
    return [...byStaff.entries()]
      .map(([staffId, g]) => ({
        staffId,
        name: g.name,
        rows: [...g.rows].sort(
          (a, b) =>
            new Date(a.clock_in_at).getTime() - new Date(b.clock_in_at).getTime(),
        ),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);

  const replaceEntry = (updated: TimeclockEntry): void => {
    setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
  };

  // ── Edit ─────────────────────────────────────────────────────────────

  const openEdit = (entry: TimeclockEntry): void => {
    setEditError(null);
    setEditDraft({
      entry,
      clockIn: isoToLocalInput(entry.clock_in_at),
      clockOut: isoToLocalInput(entry.clock_out_at),
      reason: '',
    });
  };

  const editChanged = (d: EditDraft): boolean =>
    d.clockIn !== isoToLocalInput(d.entry.clock_in_at) ||
    d.clockOut !== isoToLocalInput(d.entry.clock_out_at);

  const submitEdit = async (): Promise<void> => {
    if (!editDraft) return;
    const inIso = localInputToIso(editDraft.clockIn);
    const outIso = localInputToIso(editDraft.clockOut);
    if (!inIso) {
      setEditError('Enter a clock-in time.');
      return;
    }
    setEditPending(true);
    setEditError(null);
    const patch: {
      clockInAt?: string;
      clockOutAt?: string;
      reason: string;
    } = { reason: editDraft.reason.trim() };
    if (editDraft.clockIn !== isoToLocalInput(editDraft.entry.clock_in_at)) {
      patch.clockInAt = inIso;
    }
    if (
      editDraft.clockOut !== isoToLocalInput(editDraft.entry.clock_out_at) &&
      outIso
    ) {
      patch.clockOutAt = outIso;
    }
    const result = await adminTimeclockApi.editEntry(
      apiUrl,
      editDraft.entry.id,
      patch,
    );
    setEditPending(false);
    if (result.error) {
      setEditError(result.error);
      return;
    }
    if (result.data) {
      replaceEntry(result.data.entry);
      setEditedIds((prev) => new Set(prev).add(result.data!.entry.id));
      setNotice(`Punch updated for ${result.data.entry.staff_name}.`);
      setEditDraft(null);
    }
  };

  // ── Void ─────────────────────────────────────────────────────────────

  const submitVoid = async (): Promise<void> => {
    if (!voidFor) return;
    setVoidPending(true);
    setVoidError(null);
    const result = await adminTimeclockApi.voidEntry(
      apiUrl,
      voidFor.id,
      voidReason.trim(),
    );
    setVoidPending(false);
    if (result.error) {
      setVoidError(result.error);
      return;
    }
    if (result.data) {
      replaceEntry(result.data.entry);
      setNotice(`Entry voided for ${result.data.entry.staff_name}.`);
      // A voided early punch leaves the waiting list (Sawyer 14, AC24).
      if (result.data.entry.early?.status === 'pending') setEarlyRefresh((n) => n + 1);
      setVoidFor(null);
      setVoidReason('');
    }
  };

  // ── Add missed entry ─────────────────────────────────────────────────

  const openAdd = (): void => {
    setAddError(null);
    setAddDraft({
      staffUserId: '',
      clockIn: `${date}T09:00`,
      clockOut: `${date}T17:00`,
      reason: '',
      mealOn: false,
      mealStart: `${date}T12:00`,
      mealEnd: `${date}T12:30`,
      mealPaid: onDutyMealPaid,
    });
    setAddOpen(true);
    if (staffRoster.length === 0) void loadRoster();
  };

  const submitAdd = async (): Promise<void> => {
    const inIso = localInputToIso(addDraft.clockIn);
    const outIso = localInputToIso(addDraft.clockOut);
    if (!addDraft.staffUserId || !inIso || !outIso) {
      setAddError('Pick a staff member and both times.');
      return;
    }
    // Sawyer 14 (E3, refuter C3): the meal window is checked HERE with the
    // server's exact sentence — a zod 400 would only read "Validation failed".
    let mealBreak: { startAt: string; endAt: string; paid: boolean } | undefined;
    if (serverHasEarly && addDraft.mealOn) {
      const mStart = localInputToIso(addDraft.mealStart);
      const mEnd = localInputToIso(addDraft.mealEnd);
      const t = (v: string) => new Date(v).getTime();
      if (
        !mStart ||
        !mEnd ||
        t(mStart) < t(inIso) ||
        t(mStart) >= t(mEnd) ||
        t(mEnd) > t(outIso)
      ) {
        setAddError(MEAL_WINDOW_MESSAGE);
        return;
      }
      mealBreak = { startAt: mStart, endAt: mEnd, paid: addDraft.mealPaid };
    }
    setAddPending(true);
    setAddError(null);
    const result = await adminTimeclockApi.addManualEntry(apiUrl, {
      staffUserId: addDraft.staffUserId,
      clockInAt: inIso,
      clockOutAt: outIso,
      reason: addDraft.reason.trim(),
      ...(mealBreak ? { mealBreak } : {}),
    });
    setAddPending(false);
    if (result.error) {
      setAddError(result.error);
      return;
    }
    if (result.data) {
      setNotice(`Entry added for ${result.data.entry.staff_name}.`);
      setAddOpen(false);
      void loadEntries();
    }
  };

  // ── Render ───────────────────────────────────────────────────────────

  const reasonOk = (r: string): boolean => r.trim().length >= 3;

  return (
    <section className="ferro-admin-page" data-testid="timeclock-manage">
      <PageHeader
        title="Punch records"
        subtitle={fmtDayLabel(date)}
        actions={
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={openAdd}
          >
            Add missed entry
          </button>
        }
      />

      {/* Day picker — secondary controls (the primary is Add missed entry). */}
      <div className="ferro-tc-toolbar">
        <button
          type="button"
          className="ferro-btn ferro-btn--outline ferro-btn--sm"
          onClick={() => setDate((d) => shiftDate(d, -1))}
          aria-label="Previous day"
        >
          &larr;
        </button>
        <label className="ferro-field ferro-tc-toolbar__date">
          <span className="ferro-field__label">Day</span>
          <input
            type="date"
            className="ferro-field__input"
            value={date}
            onChange={(e) => {
              if (e.target.value) setDate(e.target.value);
            }}
          />
        </label>
        <button
          type="button"
          className="ferro-btn ferro-btn--outline ferro-btn--sm"
          onClick={() => setDate((d) => shiftDate(d, 1))}
          aria-label="Next day"
        >
          &rarr;
        </button>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost ferro-btn--sm"
          onClick={() => setDate(localDateStr(new Date()))}
        >
          Today
        </button>
      </div>

      {notice ? (
        <div
          className="ferro-alert ferro-alert--success ferro-alert--dismissible"
          role="status"
        >
          <span>{notice}</span>
          <button
            type="button"
            className="ferro-alert__dismiss"
            onClick={() => setNotice(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {/* Sawyer 14 (E1): early clock-ins waiting for a manager, any day. */}
      <EarlyApprovalList
        apiUrl={apiUrl}
        enabled={serverHasEarly}
        refreshKey={earlyRefresh}
        onDecided={(entry, decision) => {
          const who = firstName(entry.staff_name);
          setNotice(
            decision === 'approve'
              ? `Early start approved for ${who}.`
              : `${who}'s paid time starts at ${fmtTime(entry.paid_from ?? entry.early?.scheduled_start_at ?? entry.clock_in_at)}.`,
          );
          replaceEntry(entry);
        }}
        onStale={(message) => {
          setNotice(message);
          void loadEntries();
        }}
      />

      {isLoading ? (
        <div className="ferro-skeleton-stack" data-testid="tc-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="ferro-skeleton ferro-skeleton--stat" />
          ))}
        </div>
      ) : error ? (
        <div
          className="ferro-alert ferro-alert--error ferro-alert--dismissible"
          role="alert"
        >
          <span>
            Couldn&rsquo;t load this day&rsquo;s punches &mdash; {error}
          </span>
          <button
            type="button"
            className="ferro-alert__dismiss"
            onClick={() => void loadEntries()}
          >
            Try again
          </button>
        </div>
      ) : grouped.length === 0 ? (
        /* Empty teaching state (P2): what this is + what to do first. */
        <div className="ferro-tc-empty" data-testid="tc-empty">
          <p className="ferro-tc-empty__lead">
            No punches for {fmtDayLabel(date)}.
          </p>
          <p className="ferro-tc-empty__hint">
            Staff clock in from their own login or the lobby kiosk &mdash; set
            a kiosk up under <strong>Time clock devices</strong> below. Forgot
            punches can be added with &ldquo;Add missed entry&rdquo; above.
          </p>
        </div>
      ) : (
        <div className="ferro-tc-days">
          {grouped.map((group) => (
            <div
              key={group.staffId}
              className="ferro-tc-staff"
              data-testid={`tc-staff-${group.staffId}`}
            >
              <h2 className="ferro-tc-staff__name">{group.name}</h2>
              <ul className="ferro-tc-entries">
                {group.rows.map((entry) => {
                  const voided = entry.voided_at !== null;
                  const open = !voided && entry.clock_out_at === null;
                  const inSrc = sourceLabel(entry.clock_in_source);
                  const outSrc = sourceLabel(entry.clock_out_source);
                  const chip = earlyChip(entry);
                  // P5: the real punch stays the clock-in time; the paid
                  // duration is shown separately when pay starts later.
                  const paidFrom =
                    entry.paid_from && entry.paid_from !== entry.clock_in_at
                      ? entry.paid_from
                      : null;
                  return (
                    <li
                      key={entry.id}
                      className={`ferro-tc-entry${voided ? ' ferro-tc-entry--voided' : ''}`}
                      data-testid={`tc-entry-${entry.id}`}
                    >
                      <div className="ferro-tc-entry__main">
                        <span className="ferro-tc-entry__times">
                          {fmtTime(entry.clock_in_at)}
                          {' – '}
                          {entry.clock_out_at ? (
                            fmtTime(entry.clock_out_at)
                          ) : open ? (
                            <span
                              className="ferro-tc-live"
                              data-testid={`tc-live-${entry.id}`}
                            >
                              Still clocked in
                            </span>
                          ) : (
                            '—'
                          )}
                        </span>
                        {entry.clock_out_at && !voided ? (
                          <span className="ferro-tc-entry__duration">
                            {paidFrom
                              ? `${durationLabel(paidFrom, entry.clock_out_at)} paid`
                              : durationLabel(entry.clock_in_at, entry.clock_out_at)}
                          </span>
                        ) : null}
                        {inSrc ? (
                          <span
                            className={`ferro-tc-chip ferro-tc-chip--${entry.clock_in_source}`}
                          >
                            {inSrc}
                          </span>
                        ) : null}
                        {outSrc && outSrc !== inSrc ? (
                          <span
                            className={`ferro-tc-chip ferro-tc-chip--${entry.clock_out_source ?? ''}`}
                          >
                            out: {outSrc}
                          </span>
                        ) : null}
                        {editedIds.has(entry.id) ? (
                          <span
                            className="ferro-tc-chip ferro-tc-chip--edited"
                            data-testid={`tc-edited-${entry.id}`}
                          >
                            Edited
                          </span>
                        ) : null}
                        {chip && !voided ? (
                          <span
                            className={`ferro-tc-chip ${chip.pending ? 'ferro-tc-chip--early-pending' : 'ferro-tc-chip--early-decided'}`}
                            data-testid={`tc-early-chip-${entry.id}`}
                          >
                            {chip.label}
                          </span>
                        ) : null}
                      </div>

                      {entry.breaks.length > 0 ? (
                        <ul className="ferro-tc-breaks">
                          {entry.breaks.map((b) => (
                            <li key={b.id} className="ferro-tc-breaks__item">
                              {breakLine(b)}
                            </li>
                          ))}
                        </ul>
                      ) : null}

                      {voided ? (
                        /* Struck-through + reason, NO edit affordance —
                           the record stays visible (P5). */
                        <p className="ferro-tc-entry__voidnote">
                          Voided &mdash; {entry.void_reason ?? 'no reason recorded'}
                        </p>
                      ) : (
                        <div className="ferro-tc-entry__actions">
                          <button
                            type="button"
                            className="ferro-btn ferro-btn--sm ferro-btn--outline"
                            onClick={() => openEdit(entry)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="ferro-btn ferro-btn--sm ferro-btn--danger"
                            onClick={() => {
                              setVoidError(null);
                              setVoidReason('');
                              setVoidFor(entry);
                            }}
                          >
                            Void
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* ── #427 W1 — Time clock admin (kiosk registry / allowlist /
          PINs). Lives HERE, not on /staff: the section is gated on
          payroll.clock:write:all (owner + manager) while /staff is
          owner-only — the manager who sets up the lobby tablet (spec
          W1 S5) must be able to reach it. Management chrome below the
          day view; its triggers render secondary so "Add missed entry"
          stays this page's single filled-primary (P2). */}
      <TimeclockAdminSection config={config} />

      {/* ── Edit modal — datetime-local + REQUIRED reason (audit, D2) ── */}
      <Modal
        isOpen={editDraft !== null}
        onClose={() => setEditDraft(null)}
        title={editDraft ? `Edit punch — ${editDraft.entry.staff_name}` : 'Edit punch'}
      >
        {editDraft ? (
          <form
            className="ferro-form"
            onSubmit={(e) => {
              e.preventDefault();
              void submitEdit();
            }}
          >
            <label className="ferro-field">
              <span className="ferro-field__label">Clock in</span>
              <input
                type="datetime-local"
                className="ferro-field__input"
                value={editDraft.clockIn}
                onChange={(e) =>
                  setEditDraft({ ...editDraft, clockIn: e.target.value })
                }
                required
              />
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">Clock out</span>
              <input
                type="datetime-local"
                className="ferro-field__input"
                value={editDraft.clockOut}
                onChange={(e) =>
                  setEditDraft({ ...editDraft, clockOut: e.target.value })
                }
              />
              {editDraft.entry.clock_out_at === null ? (
                <span className="ferro-field__hint">
                  This entry is still open &mdash; set a clock-out to close it.
                </span>
              ) : null}
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">
                Why is this changing? (required, kept in the audit trail)
              </span>
              <textarea
                className="ferro-field__input ferro-tc-reason"
                value={editDraft.reason}
                onChange={(e) =>
                  setEditDraft({ ...editDraft, reason: e.target.value })
                }
                rows={2}
                required
              />
            </label>
            {editError ? (
              <p className="ferro-form__error" role="alert">
                {editError}
              </p>
            ) : null}
            <div className="ferro-tc-modal-actions">
              <button
                type="button"
                className="ferro-btn ferro-btn--outline"
                onClick={() => setEditDraft(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="ferro-btn ferro-btn--primary"
                disabled={
                  editPending ||
                  !reasonOk(editDraft.reason) ||
                  !editChanged(editDraft)
                }
              >
                {editPending ? 'Saving…' : 'Save correction'}
              </button>
            </div>
          </form>
        ) : null}
      </Modal>

      {/* ── Void modal — reason required, soft void (D2) ── */}
      <Modal
        isOpen={voidFor !== null}
        onClose={() => setVoidFor(null)}
        title={voidFor ? `Void punch — ${voidFor.staff_name}` : 'Void punch'}
      >
        {voidFor ? (
          <form
            className="ferro-form"
            onSubmit={(e) => {
              e.preventDefault();
              void submitVoid();
            }}
          >
            <p className="ferro-tc-void-copy">
              Voiding removes this punch from hours, but the record stays
              visible with your reason &mdash; nothing is deleted.
            </p>
            <label className="ferro-field">
              <span className="ferro-field__label">
                Why is this being voided? (required)
              </span>
              <textarea
                className="ferro-field__input ferro-tc-reason"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                rows={2}
                required
              />
            </label>
            {voidError ? (
              <p className="ferro-form__error" role="alert">
                {voidError}
              </p>
            ) : null}
            <div className="ferro-tc-modal-actions">
              <button
                type="button"
                className="ferro-btn ferro-btn--outline"
                onClick={() => setVoidFor(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="ferro-btn ferro-btn--danger"
                disabled={voidPending || !reasonOk(voidReason)}
              >
                {voidPending ? 'Voiding…' : 'Void entry'}
              </button>
            </div>
          </form>
        ) : null}
      </Modal>

      {/* ── Add missed entry — staff picked by NAME (#278), audited ── */}
      <Modal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add missed entry"
      >
        <form
          className="ferro-form"
          onSubmit={(e) => {
            e.preventDefault();
            void submitAdd();
          }}
        >
          <label className="ferro-field">
            <span className="ferro-field__label">Staff member</span>
            {rosterError ? (
              <span className="ferro-form__error" role="alert">
                Couldn&rsquo;t load the roster &mdash;{' '}
                <button
                  type="button"
                  className="ferro-btn ferro-btn--sm ferro-btn--ghost"
                  onClick={() => void loadRoster()}
                >
                  try again
                </button>
              </span>
            ) : (
              <select
                className="ferro-field__input"
                value={addDraft.staffUserId}
                onChange={(e) =>
                  setAddDraft({ ...addDraft, staffUserId: e.target.value })
                }
                required
              >
                <option value="">Pick a staff member…</option>
                {staffRoster.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.first_name} {s.last_name}
                  </option>
                ))}
              </select>
            )}
          </label>
          <label className="ferro-field">
            <span className="ferro-field__label">Clock in</span>
            <input
              type="datetime-local"
              className="ferro-field__input"
              value={addDraft.clockIn}
              onChange={(e) =>
                setAddDraft({ ...addDraft, clockIn: e.target.value })
              }
              required
            />
          </label>
          <label className="ferro-field">
            <span className="ferro-field__label">Clock out</span>
            <input
              type="datetime-local"
              className="ferro-field__input"
              value={addDraft.clockOut}
              onChange={(e) =>
                setAddDraft({ ...addDraft, clockOut: e.target.value })
              }
              required
            />
          </label>
          {/* Sawyer 14 (E3): the meal the person took on the missed day.
              Offered only when the server carries the lane (AC21). */}
          {serverHasEarly ? (
            <div className="ferro-tc-meal" data-testid="tc-meal">
              <label className="ferro-tc-check">
                <input
                  type="checkbox"
                  checked={addDraft.mealOn}
                  onChange={(e) =>
                    setAddDraft({ ...addDraft, mealOn: e.target.checked })
                  }
                />
                Add a meal break
              </label>
              {addDraft.mealOn ? (
                <>
                  <label className="ferro-field">
                    <span className="ferro-field__label">Meal break starts</span>
                    <input
                      type="datetime-local"
                      className="ferro-field__input"
                      value={addDraft.mealStart}
                      onChange={(e) =>
                        setAddDraft({ ...addDraft, mealStart: e.target.value })
                      }
                      required
                    />
                  </label>
                  <label className="ferro-field">
                    <span className="ferro-field__label">Meal break ends</span>
                    <input
                      type="datetime-local"
                      className="ferro-field__input"
                      value={addDraft.mealEnd}
                      onChange={(e) =>
                        setAddDraft({ ...addDraft, mealEnd: e.target.value })
                      }
                      required
                    />
                  </label>
                  <fieldset className="ferro-tc-radios">
                    <legend>Was the meal paid?</legend>
                    <label className="ferro-tc-check">
                      <input
                        type="radio"
                        name="tc-meal-paid"
                        checked={!addDraft.mealPaid}
                        onChange={() => setAddDraft({ ...addDraft, mealPaid: false })}
                      />
                      Unpaid (off duty)
                    </label>
                    <label className="ferro-tc-check">
                      <input
                        type="radio"
                        name="tc-meal-paid"
                        checked={addDraft.mealPaid}
                        onChange={() => setAddDraft({ ...addDraft, mealPaid: true })}
                      />
                      Paid (on duty)
                    </label>
                  </fieldset>
                </>
              ) : null}
            </div>
          ) : null}
          <label className="ferro-field">
            <span className="ferro-field__label">
              Why is this being added? (required, kept in the audit trail)
            </span>
            <textarea
              className="ferro-field__input ferro-tc-reason"
              value={addDraft.reason}
              onChange={(e) =>
                setAddDraft({ ...addDraft, reason: e.target.value })
              }
              rows={2}
              required
            />
          </label>
          {addError ? (
            <p className="ferro-form__error" role="alert">
              {addError}
            </p>
          ) : null}
          <div className="ferro-tc-modal-actions">
            <button
              type="button"
              className="ferro-btn ferro-btn--outline"
              onClick={() => setAddOpen(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ferro-btn ferro-btn--primary"
              disabled={
                addPending ||
                !addDraft.staffUserId ||
                !reasonOk(addDraft.reason)
              }
            >
              {addPending ? 'Adding…' : 'Add entry'}
            </button>
          </div>
        </form>
      </Modal>
    </section>
  );
}

export default TimeclockManage;
